import React, { useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext.tsx";
import { User, Building2, Eye, EyeOff } from "lucide-react";
import { changePassword } from "../services/partnerUser.ts";
import { useNavigate } from "react-router-dom"; // Add this import

// Move EditableField component outside of the main component
const EditableField = ({
  label,
  value,
  name,
  icon: Icon,
  isEditing,
  onChange,
}: {
  label: string;
  value: string;
  name: string;
  icon: React.ElementType;
  isEditing: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 mb-1">
      {label}
    </label>
    {isEditing ? (
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        className="input-field h-10 w-full"
      />
    ) : (
      <div className="flex items-center text-gray-900">
        <Icon className="w-5 h-5 text-gray-400 mr-2" />
        <span>{value}</span>
      </div>
    )}
  </div>
);

// Password field component with toggle visibility
const PasswordField = ({
  label,
  name,
  id,
  value,
  onChange,
  disabled = false,
  required = false,
  minLength,
  helperText,
}: {
  label: string;
  name: string;
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  minLength?: number;
  helperText?: string;
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative mt-1">
        <input
          type={showPassword ? "text" : "password"}
          name={name}
          id={id}
          className="input-field h-10 pr-10"
          value={value}
          onChange={onChange}
          required={required}
          minLength={minLength}
          disabled={disabled}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          onClick={() => setShowPassword(!showPassword)}
          disabled={disabled}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          ) : (
            <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
      {helperText && <p className="text-xs text-gray-500 mt-1">{helperText}</p>}
    </div>
  );
};

const UserProfile = () => {
  const { user, logout } = useAuth(); // Add logout
  const navigate = useNavigate(); // Add navigation hook
  // Temporarily disable editing functionality
  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    businessName: user?.partner?.name || "N/A",
    businessAddress: "Not provided", // Placeholder
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Move useCallback before the conditional return
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
    },
    []
  );

  // Removed unused handleEditToggle function

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
    // Clear previous error/success messages when user starts typing
    if (passwordError) setPasswordError("");
    if (passwordSuccess) setPasswordSuccess("");
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous messages
    setPasswordError("");
    setPasswordSuccess("");

    // Basic validation
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      setPasswordError("All password fields are required.");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long.");
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError("New password must be different from current password.");
      return;
    }

    setIsChangingPassword(true);

    try {
      const result = await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (result.error) {
        setPasswordError(result.error);
      } else {
        setPasswordSuccess(result.message || "Password changed successfully!");
        // Clear the form on success
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        // Show success message briefly, then logout and redirect
        setTimeout(() => {
          logout();
          navigate("/login", {
            state: {
              message:
                "Password changed successfully. Please log in with your new password.",
            },
          });
        }, 2000); // Wait 2 seconds to show success message
      }
    } catch (error: any) {
      console.error("Password change error:", error);
      if (error.response?.data?.error) {
        setPasswordError(error.response.data.error);
      } else if (error.message) {
        setPasswordError(error.message);
      } else {
        setPasswordError(
          "An error occurred while changing password. Please try again."
        );
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (!user) {
    return (
      <div className="p-8 bg-gray-50 min-h-full">
        <h1 className="text-2xl font-bold">User Profile</h1>
        <p>You must be logged in to view this page.</p>
      </div>
    );
  }

  // Generate initials from first and last name
  const getInitials = () => {
    const firstInitial = formData.firstName?.charAt(0)?.toUpperCase() || "";
    const lastInitial = formData.lastName?.charAt(0)?.toUpperCase() || "";
    return `${firstInitial}${lastInitial}`;
  };

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-full">
      <header className="mb-8 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900">User Profile</h1>
        <p className="text-gray-600 mt-1">
          View and manage your personal and business information.
        </p>
      </header>

      <div className="max-w-4xl mx-auto space-y-8">
        {/* Profile Information Card */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          {/* Profile Avatar */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <span className="text-3xl font-bold text-white">
                {getInitials()}
              </span>
            </div>
          </div>
          <div className="flex justify-between items-center border-b pb-4 mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Profile Information
            </h2>
            {/* Temporarily hide edit button */}
            {/* <button
              onClick={handleEditToggle}
              className="btn-primary py-2 px-3 text-sm"
            >
              {isEditing ? (
                <Save className="w-4 h-4 mr-2" />
              ) : (
                <Edit className="w-4 h-4 mr-2" />
              )}
              {isEditing ? "Save" : "Edit"}
            </button> */}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <EditableField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              icon={User}
              isEditing={false} // Force to read-only mode
              onChange={handleInputChange}
            />
            <EditableField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              icon={User}
              isEditing={false} // Force to read-only mode
              onChange={handleInputChange}
            />
            <EditableField
              label="Business Name"
              name="businessName"
              value={formData.businessName}
              icon={Building2}
              isEditing={false} // Force to read-only mode
              onChange={handleInputChange}
            />
            <EditableField
              label="Business Address"
              name="businessAddress"
              value={formData.businessAddress}
              icon={Building2}
              isEditing={false} // Force to read-only mode
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* Change Password Card */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-4">
            Change Password
          </h2>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {/* Error Message */}
            {passwordError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {passwordError}
              </div>
            )}

            {/* Success Message */}
            {passwordSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
                {passwordSuccess}
              </div>
            )}

            <PasswordField
              label="Current Password"
              name="currentPassword"
              id="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              required
              disabled={isChangingPassword}
            />

            <PasswordField
              label="New Password"
              name="newPassword"
              id="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              required
              minLength={8}
              disabled={isChangingPassword}
              helperText="Password must be at least 8 characters long."
            />

            <PasswordField
              label="Confirm New Password"
              name="confirmPassword"
              id="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              required
              disabled={isChangingPassword}
            />

            <div className="text-right">
              <button
                type="submit"
                className="btn-primary"
                disabled={isChangingPassword}
              >
                {isChangingPassword ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
